import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Search, HelpCircle, Home, FileText, ShieldCheck, Copy, X, Filter, ArrowUpRight, ArrowLeft, ArrowRight } from 'lucide-react';
import { CreatePolicyModal } from './components/CreatePolicyModal';
import { CreditApplication } from './components/CreditApplication';
import { HomePage } from './components/HomePage';
import { SearchPage } from './components/SearchPage';
export function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [policyCreated, setPolicyCreated] = useState(false);
  const [currentPage, setCurrentPage] = useState<'home' | 'policies' | 'application' | 'search'>('policies');
  const [showStartArrow, setShowStartArrow] = useState(true);
  const [headerSearch, setHeaderSearch] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [pulseHome, setPulseHome] = useState(false);
  const [pulseSearch, setPulseSearch] = useState(false);
  const homePulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchPulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const homePulseCancelled = useRef(false);
  const searchPulseCancelled = useRef(false);
  // When arriving on home page (and home pulse is done), start 3s timer for search pulse
  useEffect(() => {
    if (currentPage === 'home' && !pulseHome) {
      searchPulseCancelled.current = false;
      searchPulseTimer.current = setTimeout(() => {
        if (!searchPulseCancelled.current) {
          setPulseSearch(true);
        }
      }, 3000);
      return () => {
        if (searchPulseTimer.current) clearTimeout(searchPulseTimer.current);
      };
    }
  }, [currentPage, pulseHome]);
  // Clear pulses on navigation; cancel pending timers if user acts first
  const navigateTo = (page: 'home' | 'policies' | 'application' | 'search') => {
    // If home pulse hasn't fired yet and user navigates, cancel it permanently
    if (homePulseTimer.current) {
      clearTimeout(homePulseTimer.current);
      homePulseTimer.current = null;
      homePulseCancelled.current = true;
    }
    // If search pulse hasn't fired yet and user navigates away from home, cancel it
    if (searchPulseTimer.current && page !== 'home') {
      clearTimeout(searchPulseTimer.current);
      searchPulseTimer.current = null;
      searchPulseCancelled.current = true;
    }
    if (page === 'home') setPulseHome(false);
    if (page === 'search') setPulseSearch(false);
    setCurrentPage(page);
  };
  const handleFlowComplete = () => {
    // Start 3-second delay before showing home pulse
    homePulseCancelled.current = false;
    homePulseTimer.current = setTimeout(() => {
      if (!homePulseCancelled.current) {
        setPulseHome(true);
      }
    }, 3000);
  };
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setShowStartArrow(false);
  };
  const handleCloseModal = (created?: boolean) => {
    setIsModalOpen(false);
    if (created) {
      setPolicyCreated(true);
    }
  };
  return <div className="flex h-screen w-full bg-gray-50" data-id="element-0">
      {/* Sidebar */}
      <div className="w-16 bg-white border-r border-gray-200 flex flex-col items-center py-6 space-y-6 z-20" data-id="element-1">
        <div className="relative" data-id="element-2">
          {pulseHome && currentPage !== 'home' && <motion.div className="absolute inset-0 rounded-lg bg-indigo-700" animate={{
          opacity: [0, 0.25, 0],
          scale: [1, 1.6, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-3" />}
          <button onClick={() => navigateTo('home')} className={`relative p-2 rounded-lg transition-colors ${currentPage === 'home' ? 'text-indigo-600 bg-indigo-50' : pulseHome ? 'text-indigo-700' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-4">
            <Home className="w-5 h-5" data-id="element-5" />
          </button>
        </div>
        <div className="relative" data-id="element-6">
          {pulseSearch && currentPage !== 'search' && <motion.div className="absolute inset-0 rounded-lg bg-indigo-700" initial={{
          opacity: 0,
          scale: 1
        }} animate={{
          opacity: [0, 0.25, 0],
          scale: [1, 1.6, 1]
        }} transition={{
          duration: 2,
          repeat: 0,
          ease: 'easeInOut'
        }} data-id="element-7" />}
          <button onClick={() => navigateTo('search')} className={`relative p-2 rounded-lg transition-colors ${currentPage === 'search' ? 'text-indigo-600 bg-indigo-50' : pulseSearch ? 'text-indigo-700' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-8">
            <Search className="w-5 h-5" data-id="element-9" />
          </button>
        </div>
        <div className="relative" data-id="element-10">
          {policyCreated && currentPage === 'policies' && <motion.div animate={{
          x: [0, 8, 0]
        }} transition={{
          duration: 1.2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute left-full ml-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 whitespace-nowrap" data-id="element-11">
              <ArrowLeft className="w-5 h-5 text-gray-900 -mr-1" data-id="element-12" />
              <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-13">
                Go here
              </span>
            </motion.div>}
          <button onClick={() => navigateTo('application')} className={`p-2 rounded-lg transition-colors ${currentPage === 'application' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-14">
            <FileText className="w-5 h-5" data-id="element-15" />
          </button>
        </div>
        <button onClick={() => navigateTo('policies')} className={`p-2 rounded-lg transition-colors ${currentPage === 'policies' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-16">
          <ShieldCheck className="w-5 h-5" data-id="element-17" />
        </button>
        <div className="flex-1" data-id="element-18" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-19">
        {/* Top Navbar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4 z-10" data-id="element-20">
          <button className="text-gray-600 hover:text-gray-900" data-id="element-21">
            <Menu className="w-6 h-6" data-id="element-22" />
          </button>

          <div className="flex items-center" data-id="element-23">
            <img src="https://cdn.magicpatterns.com/uploads/7BbSTELB81jNYwNsj1BMK3/Property_1Creit_Pulse_Logo_black_2_(16).png" alt="Credit Pulse" className="h-8" data-id="element-24" />
          </div>

          <div className="flex-1 max-w-2xl mx-8" data-id="element-25">
            <div className="relative" data-id="element-26">
              <input type="text" placeholder="Search Companies" value={headerSearch} onChange={e => {
              setHeaderSearch(e.target.value);
              setShowSearchDropdown(e.target.value.length > 0);
            }} onFocus={() => {
              if (headerSearch.length > 0) setShowSearchDropdown(true);
            }} onBlur={() => {
              setTimeout(() => setShowSearchDropdown(false), 200);
            }} className="w-full px-4 py-2 pl-4 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-27" />
              {headerSearch && <button onClick={() => {
              setHeaderSearch('');
              setShowSearchDropdown(false);
            }} className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-28">
                  <X className="w-4 h-4" data-id="element-29" />
                </button>}
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-30" />

              {/* Search Dropdown */}
              {showSearchDropdown && 'dunder mifflin'.includes(headerSearch.toLowerCase().trim()) && headerSearch.trim().length > 0 && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden" data-id="element-31">
                    <button onMouseDown={e => e.preventDefault()} onClick={() => {
                setHeaderSearch('');
                setShowSearchDropdown(false);
                navigateTo('application');
              }} className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left" data-id="element-32">
                      <div className="w-1 self-stretch bg-green-500 rounded-full flex-shrink-0" data-id="element-33" />
                      <div className="flex-1 min-w-0" data-id="element-34">
                        <p className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-35">
                          DUNDER MIFFLIN PAPER COMPANY, INC.
                        </p>
                        <p className="text-xs text-indigo-500 mt-0.5" data-id="element-36">
                          1725 SLOUGH AVENUE, SCRANTON, PA, 18505
                        </p>
                        <div className="flex items-center gap-2 mt-1.5" data-id="element-37">
                          <span className="px-2 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full" data-id="element-38">
                            Active
                          </span>
                          <span className="px-2 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-full" data-id="element-39">
                            HQ
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-indigo-600 border border-indigo-300 rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-indigo-50 flex-shrink-0" data-id="element-40">
                        VIEW <ArrowRight className="w-3.5 h-3.5" data-id="element-41" />
                      </div>
                    </button>
                  </div>}
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-42">
            <button className="w-10 h-10 rounded-full border-2 border-indigo-400 flex items-center justify-center text-indigo-600 hover:bg-indigo-50" data-id="element-43">
              <HelpCircle className="w-5 h-5" data-id="element-44" />
            </button>
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium" data-id="element-45">
              IN
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto" data-id="element-46">
          {currentPage === 'home' ? <HomePage onSelectCompany={() => navigateTo('application')} data-id="element-47" /> : currentPage === 'search' ? <SearchPage onSelectCompany={() => navigateTo('application')} data-id="element-48" /> : currentPage === 'policies' ? <div className="px-8 py-6 max-w-7xl mx-auto" data-id="element-49">
              <div className="flex items-center justify-between mb-6" data-id="element-50">
                <div data-id="element-51">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-52">
                    Credit Policy Management
                  </h1>
                  <p className="text-gray-600" data-id="element-53">
                    Manage policies for New and Existing customers
                  </p>
                </div>
                <div className="relative" data-id="element-54">
                  {showStartArrow && !isModalOpen && <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute -bottom-14 right-0 flex items-center gap-1.5 whitespace-nowrap" data-id="element-55">
                      <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-56">
                        Click here to start
                      </span>
                      <ArrowUpRight className="w-5 h-5 text-gray-900 -ml-1 -mt-4" data-id="element-57" />
                    </motion.div>}
                  <button onClick={handleOpenModal} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-medium relative z-10" data-id="element-58">
                    <span className="text-lg" data-id="element-59">+</span>
                    New Policy
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6" data-id="element-60">
                <div className="flex gap-8" data-id="element-61">
                  <button className="pb-3 px-1 border-b-2 border-indigo-600 text-indigo-600 font-medium" data-id="element-62">
                    New Customer Policies
                  </button>
                  <button className="pb-3 px-1 text-gray-600 hover:text-gray-900 font-medium" data-id="element-63">
                    Existing Customer Policies
                  </button>
                </div>
              </div>

              {/* Search and Filter */}
              <div className="flex gap-4 mb-8" data-id="element-64">
                <div className="relative flex-1 max-w-sm" data-id="element-65">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-66" />
                  <input type="text" placeholder="Search policies" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-67" />
                </div>
                <button className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 text-gray-700 hover:bg-gray-50 font-medium" data-id="element-68">
                  <Filter className="w-4 h-4" data-id="element-69" />
                  Show Default Only
                </button>
              </div>

              {/* Policy Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-70">
                {/* Default Policy Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col" data-id="element-71">
                  <div className="flex items-start justify-between mb-4" data-id="element-72">
                    <div data-id="element-73">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-74">
                        Credit Pulse Default Policy
                      </h3>
                      <div className="flex items-center gap-2 mb-3" data-id="element-75">
                        <span className="px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded" data-id="element-76">
                          Default
                        </span>
                        <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded" data-id="element-77">
                          v1
                        </span>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" data-id="element-78">
                      <Copy className="w-5 h-5" data-id="element-79" />
                    </button>
                  </div>

                  <div className="space-y-1 mb-4 text-sm text-gray-600 flex-1" data-id="element-80">
                    <p data-id="element-81">Type: New Customer</p>
                    <p data-id="element-82">
                      Last updated on Mar 27, 2026 by Integration Testing User
                    </p>
                  </div>

                  <button className="w-full bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-2.5 rounded-lg font-medium mt-auto" data-id="element-83">
                    Manage Policy &gt;
                  </button>
                </div>

                {/* New Policy Card */}
                <AnimatePresence data-id="element-84">
                  {policyCreated && <motion.div initial={{
                opacity: 0,
                scale: 0.9,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col" data-id="element-85">
                      <div className="flex items-start justify-between mb-4" data-id="element-86">
                        <div data-id="element-87">
                          <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-88">
                            My Company's Credit Policy
                          </h3>
                          <div className="flex items-center gap-2 mb-3" data-id="element-89">
                            <span className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded" data-id="element-90">
                              v1
                            </span>
                          </div>
                        </div>
                        <button className="text-gray-400 hover:text-gray-600" data-id="element-91">
                          <Copy className="w-5 h-5" data-id="element-92" />
                        </button>
                      </div>

                      <div className="space-y-1 mb-4 text-sm text-gray-600 flex-1" data-id="element-93">
                        <p data-id="element-94">Type: New Customer</p>
                        <p data-id="element-95">Last updated on Mar 29, 2026 by You</p>
                      </div>

                      <button className="w-full bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-2.5 rounded-lg font-medium mt-auto" data-id="element-96">
                        Manage Policy &gt;
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div> : <CreditApplication onFlowComplete={handleFlowComplete} data-id="element-97" />}
        </main>
      </div>

      {/* Modal */}
      {isModalOpen && <CreatePolicyModal onClose={handleCloseModal} data-id="element-98" />}
    </div>;
}